<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import AppDialog from '@uiKit/elements/Dialog.vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import InfoSheet, { type InfoSheetContent } from './InfoSheet.vue'
import { useMediaQuery } from '@shared/composables/useMediaQuery'

// ⓘ button that opens an InfoSheet: centred modal over a scrim on desktop,
// bottom sheet on tablet / phone. No close button — tapping outside or Esc
// dismisses it; the modal mask means only one can ever be open. The optional
// `footer` slot receives `close` so an action can dismiss before it navigates.
// The optional `icon` slot replaces the default ⓘ glyph.

interface Props {
  content: InfoSheetContent
  /** Accessible name for the icon button. */
  label: string
  /** Icon size in px (16 on metric tiles, 24 on KPI tiles). */
  size?: number
}

const props = withDefaults(defineProps<Props>(), { size: 16 })

const MOBILE_BREAKPOINT_QUERY = '(max-width: 992px)'
const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY)
const isOpen = ref(false)

const close = () => {
  isOpen.value = false
}

// Touch target stays ≥ 40px whatever the icon size.
const MIN_HIT_AREA_PX = 40
const hitInset = `${-Math.max(0, (MIN_HIT_AREA_PX - props.size) / 2)}px`
</script>

<template>
  <!-- click.stop: the icon usually sits inside a clickable card / row. -->
  <Box
    is="button"
    flex
    align="center"
    justify="center"
    type="button"
    class="info-button"
    :style="{ width: `${size}px`, height: `${size}px`, '--info-hit-inset': hitInset }"
    :aria-label="label"
    aria-haspopup="dialog"
    @click.stop="isOpen = true"
    @keydown.enter.stop
    @keydown.space.stop
  >
    <!-- `icon` slot: a caller-supplied glyph (e.g. a design-exported SVG). -->
    <slot name="icon">
      <PiIcon name="info-circle" class="info-button__icon" :style="{ fontSize: `${size - 2}px` }" />
    </slot>
  </Box>

  <Drawer
    v-if="isMobile"
    :model-value="isOpen"
    position="bottom"
    width="100%"
    :show-close-icon="false"
    @update:model-value="(visible) => !visible && close()"
  >
    <Box class="info-button__handle" aria-hidden="true" />
    <InfoSheet :content="content">
      <template #footer><slot name="footer" :close="close" /></template>
    </InfoSheet>
  </Drawer>
  <!-- flush: the panel owns the rounded corners and clips the sheet, so the
       white sheet never paints square corners over it. -->
  <AppDialog v-else v-model="isOpen" width="350px" :show-close-icon="false" flush>
    <InfoSheet :content="content" class="info-button__sheet--dialog">
      <template #footer><slot name="footer" :close="close" /></template>
    </InfoSheet>
  </AppDialog>
</template>

<style scoped>
.info-button {
  position: relative;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.info-button::before {
  content: '';
  position: absolute;
  inset: var(--info-hit-inset);
}

.info-button__icon {
  color: var(--p-primary-500);
}

.info-button:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
  border-radius: 50%;
}

/* No dialog header above it — give the title the breathing room the header
   used to provide (the bottom sheet keeps its grab handle instead). */
.info-button__sheet--dialog {
  padding-top: 24px;
}

.info-button__handle {
  width: 134px;
  height: 5px;
  margin: 14px auto 0;
  border-radius: 100px;
  background: var(--p-surface-300);
}
</style>
